function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function utf8ToBytes(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function bytesToUtf8(b: Uint8Array): string {
  return new TextDecoder().decode(b);
}

async function sha256Hex(s: string): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', utf8ToBytes(s));
  const arr = new Uint8Array(digest);
  let out = '';
  for (let i = 0; i < arr.length; i++) out += arr[i].toString(16).padStart(2, '0');
  return out;
}

export async function generateUserKeypair(): Promise<CryptoKeyPair> {
  return await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function exportJwk(key: CryptoKey): Promise<JsonWebKey> {
  return await window.crypto.subtle.exportKey('jwk', key);
}

export async function importPublicJwk(jwk: JsonWebKey): Promise<CryptoKey> {
  return await window.crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    true,
    ['encrypt']
  );
}

export async function importPrivateJwk(jwk: JsonWebKey, options?: { extractable?: boolean }): Promise<CryptoKey> {
  const extractable = options?.extractable ?? true;
  return await window.crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    extractable,
    ['decrypt']
  );
}

export async function derivePassphraseKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const baseKey = await window.crypto.subtle.importKey('raw', utf8ToBytes(passphrase), 'PBKDF2', false, ['deriveKey']);
  return await window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptWithAesGcm(key: CryptoKey, plaintext: Uint8Array, iv: Uint8Array): Promise<Uint8Array> {
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return new Uint8Array(ct);
}

export async function decryptWithAesGcm(key: CryptoKey, ciphertext: Uint8Array, iv: Uint8Array): Promise<Uint8Array> {
  const pt = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return new Uint8Array(pt);
}

export async function encryptPrivateJwkForStorage(
  privateJwk: JsonWebKey,
  passphrase: string,
  options?: { iterations?: number }
): Promise<{
  encrypted_private_jwk: string;
  kdf_salt_b64: string;
  kdf_iterations: number;
  kdf_hash: string;
  priv_enc_alg: string;
  priv_enc_iv_b64: string;
}> {
  const iterations = options?.iterations ?? 250000;
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await derivePassphraseKey(passphrase, salt, iterations);
  const plaintext = utf8ToBytes(JSON.stringify(privateJwk));
  const ciphertext = await encryptWithAesGcm(key, plaintext, iv);
  return {
    encrypted_private_jwk: bytesToB64(ciphertext),
    kdf_salt_b64: bytesToB64(salt),
    kdf_iterations: iterations,
    kdf_hash: 'SHA-256',
    priv_enc_alg: 'AES-GCM',
    priv_enc_iv_b64: bytesToB64(iv),
  };
}

export async function decryptPrivateJwkFromStorage(
  encrypted_private_jwk: string,
  passphrase: string,
  salt_b64: string,
  iterations: number,
  iv_b64: string
): Promise<JsonWebKey> {
  const salt = b64ToBytes(salt_b64);
  const iv = b64ToBytes(iv_b64);
  const key = await derivePassphraseKey(passphrase, salt, iterations);
  const pt = await decryptWithAesGcm(key, b64ToBytes(encrypted_private_jwk), iv);
  return JSON.parse(bytesToUtf8(pt));
}

export async function encryptFileE2EE(file: File): Promise<{
  dek: CryptoKey;
  ciphertext: Uint8Array;
  iv: Uint8Array;
}> {
  const dek = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const bytes = new Uint8Array(await file.arrayBuffer());
  const ciphertext = await encryptWithAesGcm(dek, bytes, iv);
  return { dek, ciphertext, iv };
}

export async function wrapDekForUser(publicKey: CryptoKey, dek: CryptoKey): Promise<Uint8Array> {
  const raw = new Uint8Array(await window.crypto.subtle.exportKey('raw', dek));
  const wrapped = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, raw);
  return new Uint8Array(wrapped);
}

export async function unwrapDekForUser(privateKey: CryptoKey, wrappedDek: Uint8Array): Promise<CryptoKey> {
  const raw = await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, wrappedDek);
  return await window.crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
}

/**
 * For re-wrapping: returns raw DEK bytes without ever creating an extractable AES CryptoKey.
 * This avoids "key is not extractable" errors in browsers.
 */
export async function unwrapDekBytesForUser(privateKey: CryptoKey, wrappedDek: Uint8Array): Promise<Uint8Array> {
  const raw = await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, wrappedDek);
  return new Uint8Array(raw);
}

export async function wrapDekBytesForUser(publicKey: CryptoKey, dekBytes: Uint8Array): Promise<Uint8Array> {
  const wrapped = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, dekBytes);
  return new Uint8Array(wrapped);
}

/**
 * Derive a symmetric AES-GCM key from a share-link token. The token is the
 * raw secret that travels in the share URL; only someone holding it can
 * derive the same key. Used to wrap/unwrap DEK bytes for share-link
 * envelopes (`document_file_link_envelopes`).
 */
export async function deriveLinkKeyFromToken(token: string): Promise<CryptoKey> {
  const digest = await window.crypto.subtle.digest('SHA-256', utf8ToBytes(token));
  return await window.crypto.subtle.importKey(
    'raw',
    digest,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * AES-GCM-wrap raw DEK bytes for a share-link token. Returns the IV +
 * ciphertext separately; callers serialise them however the API expects
 * (typically as two base64 fields, or a single concatenated blob).
 */
export async function wrapDekBytesForLink(
  token: string,
  dekBytes: Uint8Array
): Promise<{ iv: Uint8Array; ciphertext: Uint8Array }> {
  const key = await deriveLinkKeyFromToken(token);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await encryptWithAesGcm(key, dekBytes, iv);
  return { iv, ciphertext };
}

/**
 * Inverse of {@link wrapDekBytesForLink}. Returns the raw DEK bytes so
 * callers can re-wrap them for a user public key without ever needing
 * an extractable AES CryptoKey.
 */
export async function unwrapDekBytesForLink(
  token: string,
  ciphertext: Uint8Array,
  iv: Uint8Array
): Promise<Uint8Array> {
  const key = await deriveLinkKeyFromToken(token);
  return await decryptWithAesGcm(key, ciphertext, iv);
}

export const b64 = { bytesToB64, b64ToBytes };
export const hash = { sha256Hex };

